// Helpers for the Lab review page (src/pages/Lab.jsx), its StagePicker and
// LabDetailDrawer. Backed by Xano GET /lab (api:Bh0CIPqU) — see functions/api/lab.js.
//
// A GET /lab row is the lab record + evals (branch_id from the treatment join,
// date_appointment) + addons: labstage{name,color}, lablist{name},
// doctor{code,profile_img}, _patient{name,surname,nickname,HN}, _creator{code}.
// The accessors below tolerate a missing addon so a partial row never throws.
import { apiGet } from './api.js'
import { TH_MONTHS_ABBR } from './expense.js'

export const PER_PAGE = 50

// Year options for the filter. Values were always Gregorian — only the label used to
// add 543; it no longer does, so the picker matches the dates in the table.
export const LAB_YEARS = (() => {
  const y = new Date().getFullYear()
  return [y + 1, y, y - 1, y - 2, y - 3]
})()

const asList = (r) => (Array.isArray(r) ? r : (r?.items || []))

// ── dates ─────────────────────────────────────────────────────────────────
// lab.date / deadline / date_appointment are epoch ms (or 0/null). Render on the
// Bangkok calendar (UTC+7) in GREGORIAN years — lab work is scheduled against the
// same calendar the lab vendors quote in, so ค.ศ. is what staff read here (the rest
// of the app still shows พ.ศ.). The +7h offset then UTC getters give Bangkok
// wall-clock without a timezone lib (same trick as stock's bangkokToday).
export function fmtLabDate(ms, { withYear = true } = {}) {
  const n = Number(ms)
  if (!n) return '—'
  const b = new Date(n + 7 * 3600 * 1000)
  if (Number.isNaN(b.getTime())) return '—'
  const day = String(b.getUTCDate()).padStart(2, '0')
  const mon = TH_MONTHS_ABBR[b.getUTCMonth()]
  const yr = b.getUTCFullYear()
  return withYear ? `${day} ${mon} ${yr}` : `${day} ${mon}`
}

// A "YYYY-MM-DD" date STRING, not epoch ms — treatment.date arrives in that shape.
// Gregorian year, to match every other date on the Lab surface.
export function fmtISODate(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''))
  if (!m) return s ? String(s) : '—'
  return `${m[3]} ${TH_MONTHS_ABBR[Number(m[2]) - 1]} ${m[1]}`
}

// Numeric DD/MM/YYYY (ค.ศ.) — for the compact ledger.
export function fmtLabDateNum(ms) {
  const n = Number(ms)
  if (!n) return '—'
  const b = new Date(n + 7 * 3600 * 1000)
  if (Number.isNaN(b.getTime())) return '—'
  return `${String(b.getUTCDate()).padStart(2, '0')}/${String(b.getUTCMonth() + 1).padStart(2, '0')}/${b.getUTCFullYear()}`
}

export function fmtLabDateTime(ms) {
  const n = Number(ms)
  if (!n) return '—'
  const b = new Date(n + 7 * 3600 * 1000)
  if (Number.isNaN(b.getTime())) return '—'
  const hh = String(b.getUTCHours()).padStart(2, '0')
  const mm = String(b.getUTCMinutes()).padStart(2, '0')
  return `${fmtLabDate(ms)} ${hh}:${mm}`
}

// ── row accessors ───────────────────────────────────────────────────────────
export const labStageName = (r) => r?.labstage?.name || ''
export const labStageColor = (r) => r?.labstage?.color || '#94a3b8'
export const labVendorName = (r) => r?.lablist?.name || (r?.lablist_id ? `#${r.lablist_id}` : '—')
export const labDoctorCode = (r) => r?.doctor?.code || (r?.doctor_id ? `หมอ #${r.doctor_id}` : '—')
export const labDoctorImg = (r) => r?.doctor?.profile_img || ''

export function labPatientName(r) {
  const p = r?._patient || {}
  const full = [p.name, p.surname].filter(Boolean).join(' ')
  return full || p.nickname || (r?.patient_id ? `#${r.patient_id}` : '—')
}
export const labPatientHN = (r) => r?._patient?.HN || ''

// ── price ─────────────────────────────────────────────────────────────────
export const labPrice = (r) => Number(r?.price?.price) || 0
export const labPriceClinic = (r) => Number(r?.price?.priceClinic) || 0
export const labPriceDoctor = (r) => Number(r?.price?.priceDoctor) || 0
export const labPercentClinic = (r) => Number(r?.price?.percentClinic) || 0
export const labPercentDoctor = (r) => Number(r?.price?.percentDoctor) || 0

// Short display labels for the (long) labstage names — DISPLAY ONLY; the stored
// labstage_id / name are unchanged. Falls back to the full name.
export const STAGE_SHORT = {
  'รอส่ง LAB': 'รอส่ง',
  'ส่งงานไป LAB': 'ส่งไป LAB',
  'LAB รับงาน': 'รับงาน',
  'LAB ทำงานเสร็จ': 'ทำเสร็จ',
  'ส่ง LAB กลับ': 'ส่งกลับ',
  'ได้รับ LAB': 'ได้รับ',
  'รอสรุปยอดหักทันตแพทย์ค่า LAB': 'รอสรุปหักหมอ',
  'หักยอดทันตแพทย์แล้ว': 'หักหมอแล้ว',
}
export const stageShort = (name) => STAGE_SHORT[name] || name || ''

// ── the doctor-deduction stages (the last two) ───────────────────────────────
// "หักยอดทันตแพทย์แล้ว" is NOT an ordinary stage: reaching it means the lab cost
// has already been subtracted from that doctor's salary (salary_doctor). So:
//   • Nobody may pick it in a stage dropdown. The ONLY way in is the month-wide
//     หักยอดหมอ action (POST /api/lab/df-labstage → Xano df_doctor/labstage),
//     which walks salary_doctor.labList and moves exactly those labs — a lab the
//     payroll summary never listed must not silently look "deducted".
//   • Once a lab is there it is FROZEN — no stage may be set on it again, in the
//     picker, the drawer or a bulk change. Moving it back would leave money
//     deducted from a doctor for a lab the board says is still in progress.
export const STAGE_PENDING_DEDUCT = 'รอสรุปยอดหักทันตแพทย์ค่า LAB'
export const STAGE_DEDUCTED = 'หักยอดทันตแพทย์แล้ว'

export function stageNameOf(row, stageById) {
  return row?.labstage?.name || stageById?.[Number(row?.labstage_id)]?.name || ''
}
// A frozen row: stage change AND price edit are both refused.
export function isStageLockedRow(row, stageById) {
  return stageNameOf(row, stageById) === STAGE_DEDUCTED
}
export const isDeductedStage = (s) => (s?.name || '') === STAGE_DEDUCTED
export const isPendingDeductStage = (s) => (s?.name || '') === STAGE_PENDING_DEDUCT
// What a stage dropdown (single, bulk, drawer) may offer — everything except the
// deducted stage, which only the payroll action may set.
export const selectableStages = (stages = []) => stages.filter((s) => !isDeductedStage(s))

// ค่า Lab is editable UNTIL the doctor deduction is settled — the same final stage
// locks it (per user).
// ── Lab-technician (Lab-only role) scope ─────────────────────────────────────
// The 4 stages a technician works in are `labstage.lab === true` (ids 2-5:
// ส่งงานไป LAB → LAB รับงาน → LAB ทำงานเสร็จ → ส่ง LAB กลับ).
//
// A technician only ever handles work the clinic makes in-house, so their list is
// scoped to those two vendors. `Office หมอเอ็ม` (16) is also flagged inhouse but is
// deliberately NOT here — this is the SSDC bench, not every in-house lab.
export const LAB_ONLY_VENDOR_IDS = [7, 15]   // SSDC · Office SSDC
const LAB_ONLY_VENDORS = new Set(LAB_ONLY_VENDOR_IDS)
export const isLabOnlyVendor = (id) => LAB_ONLY_VENDORS.has(Number(id))

// The SSDC bench itself, under the name that describes WHAT it is rather than who may
// see it. Same two vendors, deliberately — `lablist.inhouse` is the wrong test, because
// `Office หมอเอ็ม` (16) carries that flag too and is somebody else's bench.
//
// Two unrelated things key off this exact pair: a Lab-only technician's vendor scope,
// and (2026-08-17) whether the add form asks for the retainer spec UP FRONT. A piece we
// make ourselves is specced by us at the counter, so the builder belongs in the create
// flow; an outside lab is told what to make in `detail` and priced later.
export const isInHouseBench = isLabOnlyVendor

// ── stage visibility by role (Ram, 2026-10-18) ───────────────────────────────
// `scope` comes from role.js#labStageScope: 'lab' | 'full' | 'staff'.
//   lab   → the 4 bench stages only (labstage.lab === true, ids 2–5).
//   full  → every stage as stored (Manager / GM / Admin).
//   staff → every stage, but LAB รับงาน (3) + LAB ทำงานเสร็จ (4) collapse into ONE virtual
//           stage, "อยู่ที่ LAB". They are the bench's internal steps; to the counter the
//           piece is simply at the lab. The rows are NOT hidden — only the label merges —
//           so no work ever disappears from the front desk.
// DISPLAY ONLY. The stored labstage_id is never rewritten, and the virtual id is never
// sent upstream: StagePicker does not offer it as a target.
export const STAGE_AT_LAB_IDS = [3, 4]
export const STAGE_AT_LAB_KEY = 'atlab'      // the virtual stage's id / filter value
export const STAGE_AT_LAB_NAME = 'อยู่ที่ LAB'
const AT_LAB = new Set(STAGE_AT_LAB_IDS)
export const isMaskedStageId = (id, scope) => scope === 'staff' && AT_LAB.has(Number(id))

// The merged stage, coloured as its first member so a badge reads like the stage it replaces.
function atLabStage(stages) {
  const first = stages.find((s) => Number(s.id) === STAGE_AT_LAB_IDS[0]) || {}
  return { ...first, id: STAGE_AT_LAB_KEY, name: STAGE_AT_LAB_NAME, virtual: true }
}

// The stage list a scope may see — filter options, group headers, pickers — in workflow
// order, with 3 + 4 replaced by the one merged entry for staff.
export function stagesForScope(stages = [], scope = 'full') {
  if (scope === 'lab') return stages.filter((s) => s.lab === true)
  if (scope !== 'staff') return stages
  const out = []
  for (const s of stages) {
    if (!AT_LAB.has(Number(s.id))) out.push(s)
    else if (Number(s.id) === STAGE_AT_LAB_IDS[0]) out.push(atLabStage(stages))
  }
  return out
}

// What this scope should SHOW for one stage id: the stored stage, or the merged one.
export function shownStage(id, stages = [], scope = 'full') {
  if (isMaskedStageId(id, scope)) return atLabStage(stages)
  return stages.find((s) => Number(s.id) === Number(id)) || null
}

// The filter / group key of a row under a scope — 'atlab' for a masked 3 or 4, else the id.
export const stageKeyFor = (id, scope) => (isMaskedStageId(id, scope) ? STAGE_AT_LAB_KEY : String(Number(id) || 0))

// "LAB ทำงานเสร็จ" — the point the piece is finished, so the point we capture who
// made it. Stage ids run in workflow order, so "from ทำเสร็จ onwards" is `>=`.
export const STAGE_LAB_DONE_ID = 4
export const isAtOrAfterLabDone = (row) => (Number(row?.labstage_id) || 0) >= STAGE_LAB_DONE_ID

// The maker (lab.retainer.maker) is an Employee id. Everyone else in the clinic only
// sees it once the piece is actually finished — before that it is not yet meaningful.
export const labMakerId = (row) => Number(row?.retainer?.maker) || 0
export function makerVisibleTo(row, { labOnly = false } = {}) {
  if (!labMakerId(row)) return false
  return labOnly || isAtOrAfterLabDone(row)
}

export const LOCKED_PRICE_STAGE = STAGE_DEDUCTED
export const isPriceLockedRow = isStageLockedRow

// Build the full price object from the two editable inputs — total (price) and
// % หัก LAB (the clinic's share percent). priceClinic = price × %, priceDoctor is the
// remainder so คลินิก + หมอ = รวม exactly, and percentDoctor = 100 − %.
export function computeLabPrice(total, percentClinic) {
  const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100
  const t = r2(total)
  let pctC = Number(percentClinic)
  if (!Number.isFinite(pctC)) pctC = 0
  pctC = Math.min(100, Math.max(0, pctC))
  const clinic = r2(t * pctC / 100)
  return { price: t, priceClinic: clinic, priceDoctor: r2(t - clinic), percentClinic: r2(pctC), percentDoctor: r2(100 - pctC) }
}

// ── stage colour → readable soft badge ──────────────────────────────────────
// Colours are arbitrary hex from the labstage table (some are near-white, e.g.
// #e5e5e5 for the "done" stage), so the badge tints the background/border from the
// colour but clamps the TEXT to stay legible on white; the dot always shows the raw
// colour with a hairline ring so a pale one is still visible.
function normHex(hex) {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(String(hex || '').trim())
  return m ? `#${m[1].toLowerCase()}` : ''
}
function rgb(hex) {
  const int = parseInt(hex.slice(1), 16)
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 }
}
export function stageStyle(hex) {
  const c = normHex(hex) || '#94a3b8'
  const { r, g, b } = rgb(c)
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  const text = lum > 0.7 ? '#475569' : c // slate-600 fallback for pale colours
  return {
    raw: c,
    badge: { color: text, backgroundColor: `rgba(${r},${g},${b},0.12)`, borderColor: `rgba(${r},${g},${b},0.30)` },
    dot: { backgroundColor: c, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)' },
  }
}

// ── retainer summary (drawer + a table tag) ──────────────────────────────────
export function hasRetainer(ret) {
  return !!(ret && (ret.retainer_upper || ret.retainer_lower || ret.retainer_type ||
    ret.retainer_preset || ret.retainer_pattern || ret.retainer_note))
}
export function retainerSummary(ret) {
  if (!ret) return ''
  const ul = [ret.retainer_upper && 'บน', ret.retainer_lower && 'ล่าง'].filter(Boolean).join('+')
  const type = ret.retainer_type || ret.retainer_preset || ''
  return [type, ul && `(${ul})`].filter(Boolean).join(' ')
}

// ── stage-history timeline ───────────────────────────────────────────────────
// The lab's log[] is the stage-change history (POST /lab/status appends one entry
// per change). Legacy/imported rows carry an empty placeholder ({timestamp:0,type:''})
// that must be dropped, else the drawer shows a blank first row.
export function stageLog(row) {
  const log = Array.isArray(row?.log) ? row.log : []
  return log
    .filter((e) => e && (Number(e.timestamp) > 0 || e.type || Number(e.labstage_id) > 0))
    .map((e) => ({
      timestamp: Number(e.timestamp) || 0,
      type: e.type || '',
      message: e.message || '',
      labstage_id: Number(e.labstage_id) || 0,
      employee_id: Number(e.employee_id) || 0,
    }))
    .sort((a, b) => a.timestamp - b.timestamp)
}

// ── summary fetch (the Expense pattern) ──────────────────────────────────────
// Fetch the filtered set once (baseParams must NOT include labstage/page/per_page)
// so the stage chips can show a count for EVERY stage; the table then applies the
// stage filter client-side. Capped: page 1 tells us pageTotal, the rest fetch in
// parallel up to maxPages; dedupe by id. Returns { rows, itemsTotal, truncated }.
export async function fetchLabForSummary(baseParams, { perPage = 200, maxPages = 25 } = {}) {
  const first = await apiGet('/api/lab', { ...baseParams, page: 1, per_page: perPage })
  const items = asList(first)
  const totalPages = Math.max(1, Number(first?.pageTotal) || 1)
  const itemsTotal = Number(first?.itemsTotal) || items.length
  const cap = Math.min(totalPages, maxPages)
  const acc = [...items]
  if (cap > 1 && items.length) {
    const rest = await Promise.all(
      Array.from({ length: cap - 1 }, (_, i) =>
        apiGet('/api/lab', { ...baseParams, page: i + 2, per_page: perPage }).catch(() => null)),
    )
    for (const r of rest) acc.push(...asList(r))
  }
  const seen = new Set()
  const rows = []
  for (const r of acc) {
    const id = r?.id
    if (id != null) { if (seen.has(id)) continue; seen.add(id) }
    rows.push(r)
  }
  return { rows, itemsTotal, truncated: totalPages > maxPages }
}

// ── due date + card state (patient Lab page) ─────────────────────────────────
// `deadline` is NOT an independent SLA. Measured 2026-08-12 over the 30 newest
// rows: it equals `date` on 25 of them, is EARLIER on the other 5, and is 0 on
// older imports — and nothing in this repo ever writes it (`/api/lab/assign`
// forwards lablist/date/year/month/doctor/repair only). So it is treated as an
// optional override of `date`, and a "เลยกำหนด N วัน" countdown is only ever shown
// where §labCardState has already established the row is recent enough for the
// number to mean something.
const BKK_MS = 7 * 3600 * 1000
const DAY_MS = 86400000
// Bangkok calendar-day index for an epoch-ms value (same +7h trick as fmtLabDate).
const bkkDay = (ms) => Math.floor((Number(ms) + BKK_MS) / DAY_MS)

export const labDue = (r) => Number(r?.deadline) || Number(r?.date) || 0

// ── the two dates a lab write sends, from the ONE a human chooses ────────────
// (Ram, 2026-09-07. This REPLACED a model where `lab.date` was the chosen one.)
//
//   `deadline`  is the choice — when the piece must be at the clinic, or finished on the
//               bench. For an outside lab it may sit BEFORE the visit, when the clinic
//               wants the work in hand early. For in-house work it is the QUOTA date:
//               the bench takes only so many pieces a day and this is what that load is
//               counted on (§labDue, §buildDayLoad, Xano api 648).
//   `date`      is the handover day. It FOLLOWS the linked appointment — api 194 rewrites
//               it from appointment.start_date on any write carrying appointment_id — and
//               only stands in for it when there is no link to follow.
//
// Which is why nothing here fights api 194 any more: the endpoint's rewrite IS the rule.
//
//   linked   → date = the appointment's day (upstream will set it; sent so year/month,
//              which upstream does NOT derive, agree with it). While the link is not
//              moving, `storedDateMs` is resent instead, so an unrelated edit cannot
//              quietly reset a 12:30 lab to 00:00 — the picker speaks days, the row
//              remembers a time.
//   unlinked → date = the deadline. There is no appointment to name the handover, so the
//              best the record can say is "due then".
export function labDatesForWrite({ deadlineMs = 0, apptMovingToMs = 0, storedDateMs = 0, linked = false } = {}) {
  const deadline = Number(deadlineMs) || 0
  const moving = Number(apptMovingToMs) || 0
  const stored = Number(storedDateMs) || 0
  const date = linked ? (moving || stored || deadline) : (deadline || stored)
  return { date, deadline, ...labYearMonth(date) }
}

// Bangkok {year, month} for an epoch-ms date. Xano does NOT derive lab.year/lab.month
// from lab.date — they are plain columns backing the /lab board's ปี/เดือน filter — so
// every writer that sends a date has to send these alongside it or the piece goes
// missing from the month it was actually made in.
export function labYearMonth(ms) {
  const n = Number(ms) || 0
  if (!n) return { year: 0, month: 0 }
  const b = new Date(n + BKK_MS)
  return { year: b.getUTCFullYear(), month: b.getUTCMonth() + 1 }
}

// Whole days from the due date to today, Bangkok. Positive = overdue. null = undated.
export function labAgeDays(r) {
  const due = labDue(r)
  if (!due) return null
  return bkkDay(Date.now()) - bkkDay(due)
}

// Human urgency, modelled on lib/ticket.js#tFollowupRel (same wording, same tones).
export function labDueRel(r) {
  const age = labAgeDays(r)
  if (age == null) return null
  if (age > 0) return { label: `เลยกำหนด ${age} วัน`, tone: 'overdue' }
  if (age === 0) return { label: 'ครบวันนี้', tone: 'today' }
  return { label: `อีก ${-age} วัน`, tone: 'future' }
}

// Tones for labDueRel() live in lib/ticket.js as REL_TONES ({text, dot}) — the same
// concept (relative-date urgency) with the same three keys, already used by the
// ticket list. A second, differently-shaped copy under a near-identical name is a
// silent-undefined-className trap, so there isn't one.

// ── which rows earn a card on the patient page ──────────────────────────────
// ONE rule, and it is the money rule (Ram, 2026-08-18): a lab belongs to ประวัติงาน Lab
// only once it reaches **หักยอดทันตแพทย์แล้ว (stage 8)**. That stage means its cost has
// been taken out of a doctor's salary — the case is closed and paid. Anything else is
// still open business and stays in ต้องติดตาม, however old it is.
//
// This REPLACED a stage × age rule (stage 6 ≤30d, stages 1–5 ≤90d, else history). That
// version filed two kinds of row as "history" that were nothing of the sort: a stage-7
// lab waiting on payroll, and any piece that had simply been open a long time. Being
// forgotten is not the same as being finished, and the old rule made the two look alike.
//
// The age windows survive for TONE, not for membership — see `isLabStale`. A 2024 lab
// still on stage 1 now earns a card, but a slate "ค้างมาแล้ว 870 วัน" one, never a rose
// "เลยกำหนด 870 วัน" alarm. That distinction is why the windows existed and it is kept.
export const STAGE_RECEIVED_ID = 6        // ได้รับ LAB — the piece is at our counter
export const STAGE_PENDING_DEDUCT_ID = 7  // รอสรุปยอดหักทันตแพทย์ค่า LAB
export const STAGE_DEDUCTED_ID = 8        // หักยอดทันตแพทย์แล้ว — the only "done"
export const READY_WINDOW_DAYS = 30
export const INFLIGHT_WINDOW_DAYS = 90

export function labCardState(r) {
  const stage = Number(r?.labstage_id) || 0
  if (stage === STAGE_DEDUCTED_ID) return null            // settled → the ledger
  if (stage === STAGE_RECEIVED_ID) return 'ready'         // at the counter, call them
  if (stage === STAGE_PENDING_DEDUCT_ID) return 'settling' // made; waiting on payroll
  return 'inflight'                                        // 1–5, and stage-less rows
}

// Open far past any plausible turnaround — or never dated at all. Drives the SLATE tag
// and suppresses the overdue countdown: "เลยกำหนด 870 วัน" in red is noise on exactly
// the long-standing ortho patients this page exists for.
export const isLabStale = (r) => {
  if (Number(r?.labstage_id) === STAGE_DEDUCTED_ID) return false
  const age = labAgeDays(r)
  return age == null || age > INFLIGHT_WINDOW_DAYS
}

// What to call the piece, in descending order of how much it actually says.
//
// `detail` wins. It is multi-line ("RTN U/L\nPlate: …\nSTK: …") so only its first
// line is used, but it is the only field that distinguishes one piece from another:
// 18 of the 25 newest live rows carry a retainer, and `retainerSummary` collapses
// every one of them into three strings ("Wraparound (บน+ล่าง)", "Clear (บน+ล่าง)",
// "Wraparound (ล่าง)") — a year group would render as a column of identical titles
// while "ฟันปลอมบนรีเทนเนอร์ #16" sat unused in `detail`. `production_type` is junk
// (only "" or "retainer" across the same sample) and comes last.
export function labTitle(r) {
  const first = String(r?.detail || '').split('\n').map((s) => s.trim()).find(Boolean)
  if (first) return first
  if (hasRetainer(r?.retainer)) {
    const s = retainerSummary(r.retainer)
    if (s) return s
  }
  if (r?.production_type) return r.production_type
  return r?.id ? `Lab #${r.id}` : '—'
}

// ── per-day workload, for the กำหนดส่ง picker ─────────────────────────────────
// Staff pick a กำหนดส่ง for a new piece; without knowing what is already booked on it they
// stack five crowns on one day and none on the next. These turn a month of lab rows into
// a count per Bangkok day.
//
// 🔑 A PIECE OCCUPIES ITS DUE DAY, NOT ITS HANDOVER DAY (Ram, 2026-09-07). The day comes
// from §labDue — `deadline`, falling back to `date` — because `deadline` is the date a
// human chooses and, for the in-house bench, the date its daily quota is counted on;
// `date` merely follows whatever appointment the piece is linked to. The fallback is what
// keeps the old Bubble-era imports (deadline 0) from vanishing out of the calendar the way
// the year/month rows did — Xano api 648 matches its window on EITHER column for the same
// reason. NO CEILING is enforced: the bench runs ~11 pieces a day but Ram wants that
// flexible, not capped, so this reports load and the picker shades it.
//
// ⚠️ งานแก้ NO LONGER COUNTS (Ram, 2026-09-07) — this REVERSES the 2026-08-18 rule that
// counted it. The number stands in for หัก Lab หมอ, the deduction df_doctor/gen3 takes off
// a doctor's fee, and a remake carries no ค่า Lab so it deducts nothing. A day's number is
// therefore the NEW pieces only. `repair` is still returned and the picker still flags a
// day that carries remakes — the bench-occupancy argument behind the old rule was not
// wrong, it just is not what this number is for.
//
// ⚠️ THE MONTH FILTER USED TO BE THE WRONG COLUMN. This used to be fed by
// fetchLabForSummary({year, month}), which filters `lab.year` / `lab.month` — columns that
// belong to PAYROLL, not to the calendar. df_doctor/gen2 rewrites them when it moves an
// unsettled in-house lab into the month it is generating and leaves `lab.date` alone, so
// they answer "which payroll month is this charged to", not "when was it made". Measured
// 2026-09-07 on lablist 7 (SSDC in-house): 2026-09 matched 194 rows by those columns
// against 287 by real date, 99 of them dated in September but charged elsewhere — a third
// of the month missing. 2026-08: 568 by column, 499 by date, 34 drifted. It lost rows at
// both ends, because anything fetched but dated outside the grid was dropped as well.
// GET /lab has no date filter, hence GET /api/lab/day-load (Xano api 648).
//
// ⚠️ These counts are still STRUCTURALLY LOW, for the one remaining reason: a lab with
// treatment_id = 0 is excluded (8,399 of 38,742 rows as of 2026-08-12), matching what the
// /lab board itself shows and reflecting that such a piece has no doctor to deduct from.
// Deleted rows are out too. In the 2026-09 window that exclusion removed nothing at all
// (287 rows either way), so the drift above was the whole of the error staff were seeing.

// 'YYYY-MM-DD' on the Bangkok calendar — the grouping key, and what an <input type="date">
// speaks, so the two never need converting between each other.
export function labDayKey(ms) {
  const n = Number(ms) || 0
  if (!n) return ''
  const b = new Date(n + BKK_MS)
  const p = (x) => String(x).padStart(2, '0')
  return `${b.getUTCFullYear()}-${p(b.getUTCMonth() + 1)}-${p(b.getUTCDate())}`
}

// rows → Map<'YYYY-MM-DD', {count, repair}>. Grouped on `date`, the column the form
// actually sets — NOT on year/month, which Xano never derives and which payroll rewrites.
//
// `count` EXCLUDES งานแก้ and `repair` counts them, so the two are disjoint and a day's
// physical piece count is `count + repair`. The old shape called the inclusive figure
// `total`; the key was renamed rather than redefined so that any reader still expecting
// the old meaning gets `undefined` instead of a number that quietly changed underneath.
export function buildDayLoad(rows) {
  const m = new Map()
  for (const r of rows || []) {
    const key = labDayKey(labDue(r))
    if (!key) continue
    const cur = m.get(key) || { count: 0, repair: 0 }
    if (r?.repair) cur.repair += 1
    else cur.count += 1
    m.set(key, cur)
  }
  return m
}

// One month of {date, deadline, repair} for one vendor. `from`/`to` are epoch ms marking a
// half-open Bangkok window [from, to) — the same bounds the picker already computes to lay
// out its grid, so the two can never disagree about which day a piece falls on. Upstream
// matches the window on EITHER date column and caps the span at ~45 days; which of the two
// a row is then counted on is §labDue's call, made here rather than in XanoScript.
export async function fetchLabDayLoad({ lablistId, from, to }) {
  const res = await apiGet('/api/lab/day-load', {
    lablist_id: Number(lablistId) || 0,
    from_ms: Number(from) || 0,
    to_ms: Number(to) || 0,
  })
  return asList(res?.rows ?? res)
}

// Bangkok calendar year of a lab row — the ledger's grouping key.
export function labYear(r) {
  const ms = Number(r?.date)
  if (!ms) return 0
  return new Date(ms + BKK_MS).getUTCFullYear()
}

// ── ตาม Lab: the follow-up view, keyed on lab.date ───────────────────────────
// (Ram, 2026-09-13. The companion to หัก Lab, which is keyed on the PAYROLL columns.)
//
// 🚨 THE WHOLE REASON THERE ARE TWO VIEWS. /lab's ปี/เดือน filter matches
// `lab.year` / `lab.month`, and those are payroll columns — df_doctor/gen2 rewrites
// them to move an unsettled in-house lab into the month it is generating, and never
// touches `lab.date`. So they answer "which payroll month is this charged to" and
// CANNOT answer "which pieces are due this month". Measured live 2026-09-13,
// clinic-wide: September 2026 held **614 labs by lab.date against 425 by the payroll
// columns** — 189 pieces missing from the month they are actually handed over in.
// Hence Xano api 652 (`claude` group) and §fetchLabByDate.

// Stage 6 ได้รับ LAB is the first stage at which the appliance is physically at our
// counter; 1–5 are รอส่ง → ส่ง LAB กลับ, i.e. still out. There is no "handed to the
// patient" stage, so 6+ means "in the clinic", which is exactly the question here.
export const STAGE_IN_CLINIC_FROM = 6
export const labReceived = (r) => Number(r?.labstage_id) >= STAGE_IN_CLINIC_FROM

// Bangkok [from, to) epoch-ms window for a ปี/เดือน filter pair. `month` empty = ทั้งปี.
// Built here rather than in XanoScript so the +7h day boundary is decided in ONE place
// (same reason as §buildDayLoad).
export function bkkMonthWindow(year, month) {
  const y = Number(year) || 0
  if (!y) return null
  const m = Number(month) || 0
  const at = (yy, mm) => Date.UTC(yy, mm, 1) - BKK_MS   // 00:00 Bangkok on the 1st
  return m ? { from: at(y, m - 1), to: at(y, m) } : { from: at(y, 0), to: at(y + 1, 0) }
}

// One Bangkok calendar day as a [from, to) epoch-ms window, from the 'YYYY-MM-DD' the
// booking calendar already speaks. Same +7h convention as §bkkMonthWindow.
export function bkkDayWindow(dayKey) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dayKey || ''))
  if (!m) return null
  const from = Date.UTC(+m[1], +m[2] - 1, +m[3]) - BKK_MS
  return { from, to: from + DAY_MS }
}

// ── is this piece late, and against WHICH date? (Ram, 2026-09-13) ────────────
// Both, at two volumes — because the two dates fail differently:
//   • past `deadline`, not received  → AMBER. The internal target slipped. This is the
//     one staff can still act on: the patient has not arrived yet.
//   • past `date`, not received      → RED. `date` is the handover day and follows the
//     appointment, so this reads "the visit happened and we had nothing to fit".
// Red wins when both are true, which is most of the time: `deadline` may never run past
// the appointment (LabDayPicker's maxDay cap), so date-overdue implies deadline-overdue.
// `deadline` is 0 on Bubble-era imports, so the amber arm is skipped rather than firing
// on the epoch.
export function labChaseState(r, nowMs = Date.now()) {
  if (labReceived(r)) return { level: 'done', days: 0, label: 'ได้รับแล้ว' }
  const today = bkkDay(nowMs)
  const date = Number(r?.date) || 0
  const dl = Number(r?.deadline) || 0
  const dDate = date ? bkkDay(date) - today : null
  if (dDate != null && dDate < 0) return { level: 'overdue', days: -dDate, label: `เลยวันนัด ${-dDate} วัน` }
  if (dDate === 0) return { level: 'overdue', days: 0, label: 'ส่งมอบวันนี้' }
  const dDl = dl ? bkkDay(dl) - today : null
  if (dDl != null && dDl < 0) return { level: 'late', days: -dDl, label: `เลยกำหนดส่ง ${-dDl} วัน` }
  if (dDl === 0) return { level: 'late', days: 0, label: 'ครบกำหนดส่งวันนี้' }
  if (dDate != null) return { level: 'due', days: dDate, label: `อีก ${dDate} วัน` }
  return { level: 'due', days: 0, label: '—' }
}

// Tailwind tones for §labChaseState. `done` is deliberately quiet: on the full-window
// view most rows are received, and colouring those green would drown the two that are not.
export const CHASE_TONES = {
  overdue: { chip: 'text-rose-700 bg-rose-50 ring-rose-200', dot: 'bg-rose-500' },
  late:    { chip: 'text-amber-700 bg-amber-50 ring-amber-200', dot: 'bg-amber-500' },
  due:     { chip: 'text-slate-500 bg-slate-50 ring-slate-200', dot: 'bg-slate-300' },
  done:    { chip: 'text-emerald-700 bg-emerald-50 ring-emerald-200', dot: 'bg-emerald-500' },
}

// Same paging walk as §fetchLabForSummary, against api 652 instead of GET /lab. The row
// shape is identical by construction, so every accessor above works on either.
export async function fetchLabByDate(baseParams, { perPage = 200, maxPages = 25 } = {}) {
  const first = await apiGet('/api/lab/by-date', { ...baseParams, page: 1, per_page: perPage })
  const items = asList(first)
  const totalPages = Math.max(1, Number(first?.pageTotal) || 1)
  const itemsTotal = Number(first?.itemsTotal) || items.length
  const cap = Math.min(totalPages, maxPages)
  const acc = [...items]
  if (cap > 1 && items.length) {
    const rest = await Promise.all(
      Array.from({ length: cap - 1 }, (_, i) =>
        apiGet('/api/lab/by-date', { ...baseParams, page: i + 2, per_page: perPage }).catch(() => null)),
    )
    for (const r of rest) acc.push(...asList(r))
  }
  const seen = new Set()
  const rows = []
  for (const r of acc) {
    const id = r?.id
    if (id != null) { if (seen.has(id)) continue; seen.add(id) }
    rows.push(r)
  }
  return { rows, itemsTotal, truncated: totalPages > maxPages }
}

// ── which appointments have a lab? (Ram, 2026-09-13) ─────────────────────────
// 🔑 TWO KEYS, BECAUSE ONE IS NOT ENOUGH. `lab.appointment_id` is the explicit link and
// is exact — but it is SET ON ONLY A QUARTER OF ROWS. Measured live 2026-09-13 over the
// 614 labs dated in September 2026: 149 (24%) carry an appointment_id. Falling back to
// "same patient, same Bangkok day" — which works because api 194 rewrites `lab.date`
// from `appointment.start_date` — finds a visit for 297 (48%), double the coverage, and
// lifts the appointments that light up from 130 to 244.
//
// The explicit link WINS where the two disagree: 6 of those 149 point at an appointment
// on a different day than `lab.date`, legacy rows where the pair was deliberately left
// disagreeing. An id someone stored beats a day two records happen to share.
//
// ⚠️ The day rule is an INFERENCE and 33 of the 297 are ambiguous — the patient had more
// than one appointment that day, so the piece is attributed to each of them. That is the
// right failure for a "there is lab work around this visit" flag and the wrong one for
// anything that counts or bills. Do not reuse this to attribute cost.
export function buildApptLabIndex(labs) {
  const byAppt = new Map()   // appointment_id → rows[]
  const byPatientDay = new Map()  // `${patient_id}:${bkkDay}` → rows[]
  const byPatient = new Map()     // patient_id → rows[]  (for the carry-over rule)
  const add = (map, key, r) => {
    const cur = map.get(key)
    if (cur) cur.push(r); else map.set(key, [r])
  }
  for (const r of labs || []) {
    const ap = Number(r?.appointment_id) || 0
    if (ap) add(byAppt, ap, r)
    const pid = Number(r?.patient_id) || 0
    const d = Number(r?.date) || 0
    if (pid) add(byPatient, pid, r)
    if (pid && d) add(byPatientDay, `${pid}:${bkkDay(d)}`, r)
  }
  return { byAppt, byPatientDay, byPatient }
}

/**
 * CARRY-OVER: a piece that was due BEFORE this visit and still has not arrived
 * (Ram, 2026-09-13). Not this appointment's own lab — an older one, whose
 * `lab.date` has already passed, that the patient is about to walk in without.
 *
 * The visit itself is the deadline that matters: the patient turns up, sits down,
 * and there is nothing to fit. So it is flagged on the APPOINTMENT rather than
 * left on the lab worklist, where only the lab team would see it.
 *
 * ⚠️ Scoped to TODAY ONWARD on purpose. The rule as stated — any appointment
 * dated after the lab — also matches every past visit since the piece went
 * missing, and a red flag on history is noise nobody can act on. Widen this if
 * the clinic wants the archaeology.
 *
 * ⚠️ It marks EVERY remaining future appointment, not just the next one: with
 * only the index in hand there is no way to tell which visit the piece was meant
 * for. Over-warning is the right failure here; silence is not.
 */
export function carryOverLabsForAppt(index, appt, nowMs = Date.now()) {
  if (!index?.byPatient || !appt) return []
  const pid = Number(appt.patient_id) || 0
  const start = Number(appt.start_date) || 0
  if (!pid || !start) return []
  const apptDay = bkkDay(start)
  if (apptDay < bkkDay(nowMs)) return []
  const own = new Set(labsForAppt(index, appt).map((r) => Number(r?.id) || 0))
  return (index.byPatient.get(pid) || []).filter((r) => {
    if (own.has(Number(r?.id) || 0)) return false   // this visit's own piece, not a carry-over
    if (labReceived(r)) return false
    const d = Number(r?.date) || 0
    if (!d || bkkDay(d) >= apptDay) return false
    // ⚠️ "Earlier than this visit" is NOT the same as "late". A piece due next
    // Tuesday is earlier than a visit next Friday and is running perfectly to
    // time — flagging that red would cry wolf on ordinary bookings. It has to be
    // overdue by its OWN clock, and labChaseState is the one place that decides
    // what overdue means (its date has passed, or it is due today).
    return labChaseState(r, nowMs).level === 'overdue'
  })
}

// The labs attached to one appointment, explicit link first. Returns [] for none, so a
// caller can just check `.length`.
export function labsForAppt(index, appt) {
  if (!index || !appt) return []
  const id = Number(appt.id) || 0
  const exact = id ? index.byAppt.get(id) : null
  if (exact?.length) return exact
  const pid = Number(appt.patient_id) || 0
  const start = Number(appt.start_date) || 0
  if (!pid || !start) return []
  // Never let the day rule re-attach a piece that names a DIFFERENT appointment: that
  // row's own link is the better answer, and it belongs to the visit it names.
  const hits = index.byPatientDay.get(`${pid}:${bkkDay(start)}`) || []
  return hits.filter((r) => !Number(r?.appointment_id) || Number(r.appointment_id) === id)
}

// One appointment's lab summary for the indicator: how many pieces, and are they here?
// `received` false = at least one piece is still out, which is what the icon shouts.
export function apptLabSummary(index, appt, nowMs = Date.now()) {
  const rows = labsForAppt(index, appt)
  const carry = carryOverLabsForAppt(index, appt, nowMs)
  // A carry-over alone is worth a flag: this visit has no lab of its own, and that
  // is exactly the case where nothing would otherwise warn anyone.
  if (!rows.length && !carry.length) return null
  const pending = rows.filter((r) => !labReceived(r))
  const worstOf = (list) => list.reduce((acc, r) => {
    const s = labChaseState(r, nowMs)
    return !acc || (s.level === 'overdue' && acc.level !== 'overdue') ? s : acc
  }, null)
  return {
    count: rows.length,
    pending: pending.length,
    received: rows.length > 0 && pending.length === 0,
    worst: worstOf(pending),
    carry: carry.length,
    carryWorst: worstOf(carry),
  }
}
